import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // ✅ Mobile-safe: event delegation for copy-discount (survives DOM morph/re-render)
    this.addEventListener('pointerup', this.#handleCopyDiscount);
    this.addEventListener('click', this.#handleCopyDiscount);
    this.addEventListener('keydown', this.#handleCopyDiscountKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    this.removeEventListener('pointerup', this.#handleCopyDiscount);
    this.removeEventListener('click', this.#handleCopyDiscount);
    this.removeEventListener('keydown', this.#handleCopyDiscountKeydown);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  // ✅ Keyboard support (Enter / Space)
  #handleCopyDiscountKeydown = (e) => {
    const key = e.key;
    if (key !== 'Enter' && key !== ' ') return;

    const target = /** @type {HTMLElement} */ (e.target);
    const block = target?.closest?.('.cart__copy-discount');
    if (!block) return;

    e.preventDefault();
    this.#copyDiscountFromBlock(block);
  };

  // ✅ Pointer + Click delegation (works on mobile even when DOM is re-rendered)
  #handleCopyDiscount = (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    const block = target?.closest?.('.cart__copy-discount');
    if (!block) return;

    // prevent double-trigger (pointerup + click)
    if (e.type === 'click' && block.dataset._copiedPointer === '1') {
      block.dataset._copiedPointer = '0';
      return;
    }
    if (e.type === 'pointerup') {
      block.dataset._copiedPointer = '1';
    }

    this.#copyDiscountFromBlock(block);
  };

  async #copyDiscountFromBlock(block) {
    const code = block.getAttribute('data-discount-code') || '';
    if (!code) return;

    const iconWrap = block.querySelector('.cart__copy-discount-icon');
    const svgs = iconWrap ? iconWrap.querySelectorAll('svg') : null;
    const clipboardSvg = svgs?.[0] || null;
    const checkSvg = svgs?.[1] || null;

    const showCopiedState = () => {
      clipboardSvg?.classList.add('is-hidden');
      checkSvg?.classList.remove('is-hidden');
    };

    const resetState = () => {
      clipboardSvg?.classList.remove('is-hidden');
      checkSvg?.classList.add('is-hidden');
    };

    const publishCopied = () => {
      try {
        Shopify?.analytics?.publish?.('eirvind:discount_code_copied', {
          code,
          location: 'cart_drawer',
        });
      } catch (e) {}
    };

    try {
      // Preferred modern API (requires secure context, user gesture)
      await navigator.clipboard.writeText(code);
      showCopiedState();
      publishCopied(); // ✅ TRACK
      window.clearTimeout(block._copyResetTimer);
      block._copyResetTimer = window.setTimeout(resetState, 1800);
    } catch (err) {
      // Fallback for iOS / older browsers
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, ta.value.length);

      const ok = document.execCommand('copy');
      document.body.removeChild(ta);

      if (ok) {
        showCopiedState();
        publishCopied(); // ✅ TRACK
        window.clearTimeout(block._copyResetTimer);
        block._copyResetTimer = window.setTimeout(resetState, 1800);
      }
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}